/**
 * Deploy-version check. The build stamps its id into the bundle
 * (import.meta.env.VITE_APP_BUILD_ID) and writes the same id to
 * /version.json. A tab left open across a deploy sees a different id there
 * and reloads, so old code never keeps calling endpoints that are gone.
 */

/** No input for this long counts as idle. */
export const IDLE_AFTER_MS = 60_000;
/** How often /version.json is read (plus on focus / tab shown). */
export const VERSION_POLL_MS = 120_000;

/** The id of the running build; null in dev and tests (no check then). */
export function currentBuildId(): string | null {
  const id = import.meta.env.VITE_APP_BUILD_ID as string | undefined;
  return id && id !== "dev" ? id : null;
}

/**
 * The deployed build id, or null when it can't be read: missing file (dev,
 * preview), the SPA fallback answering with index.html, offline, bad JSON.
 * Never throws.
 */
export async function fetchDeployedBuildId(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await fetchImpl(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const type = res.headers.get("content-type") ?? "";
    if (!type.includes("json")) return null;
    const body = (await res.json()) as { version?: unknown };
    return typeof body?.version === "string" && body.version ? body.version : null;
  } catch {
    return null;
  }
}

// ── Unsaved work registry ─────────────────────────────────────────────────
// Screens with work a reload would lose (a customer being typed, cart lines
// not saved yet) register a check; the watcher asks before reloading.

type UnsavedCheck = () => boolean;
const unsavedChecks = new Set<UnsavedCheck>();

export function registerUnsavedWork(check: UnsavedCheck): () => void {
  unsavedChecks.add(check);
  return () => {
    unsavedChecks.delete(check);
  };
}

export function hasUnsavedWork(): boolean {
  for (const check of unsavedChecks) {
    try {
      if (check()) return true;
    } catch {
      // A broken check must not block or force a reload: ignore it.
    }
  }
  return false;
}

// ── Decision ──────────────────────────────────────────────────────────────

export interface ReloadState {
  /** Running build (null: dev/tests, never reload). */
  current: string | null;
  /** Deployed build from /version.json (null: unknown). */
  deployed: string | null;
  /** ms since the last user input. */
  idleMs: number;
  /** A dialog / sheet / menu is open. */
  dialogOpen: boolean;
  /** A screen reported unsaved work. */
  unsavedWork: boolean;
  /** A request that changes data is in flight (react-query mutations). */
  mutating: boolean;
  /** The kitchen display: reloads when idle without asking. */
  isKitchen: boolean;
}

/**
 * - "none": up to date (or unknown);
 * - "reload": new build and safe to reload now;
 * - "prompt": new build, not safe yet: show the banner, reload at the next
 *   idle moment (POS);
 * - "wait": new build, not safe yet, no banner (kitchen display).
 * Never "reload" while a mutation is in flight.
 */
export type ReloadDecision = "none" | "reload" | "prompt" | "wait";

export function decideReload(s: ReloadState): ReloadDecision {
  if (!s.current || !s.deployed || s.current === s.deployed) return "none";
  const idle = s.idleMs >= IDLE_AFTER_MS;
  if (s.isKitchen) {
    return idle && !s.mutating && !s.dialogOpen ? "reload" : "wait";
  }
  return idle && !s.mutating && !s.dialogOpen && !s.unsavedWork ? "reload" : "prompt";
}

/** The banner's Reload button: the user asked, but a save in flight still wins. */
export function canReloadOnRequest(s: Pick<ReloadState, "mutating">): boolean {
  return !s.mutating;
}

/** An open Radix dialog / alert dialog / sheet (they render role="dialog" | "alertdialog"). */
export function isDialogOpen(doc: Document = document): boolean {
  return !!doc.querySelector('[role="dialog"], [role="alertdialog"]');
}

export function isKitchenPath(pathname: string): boolean {
  return pathname === "/kitchen" || pathname.startsWith("/kitchen/");
}
